import { type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { FullPageSpinner } from './FullPageSpinner'
import { useAuth } from '@/lib/auth/useAuth'

/**
 * Inverse of RequireAuth. Renders children only for UNauthenticated users.
 * Anyone with a live Cognito session is bounced straight to /app — the
 * authenticated-app entry guards (RequireAuth) take over from there and
 * route to /onboarding if their profile isn't complete yet.
 *
 * Wrap /login, /signup, /forgot-password. Do NOT wrap /auth/callback or
 * /verify-email — those pages legitimately need to render while a session
 * is being established or just-created.
 *
 * Single point of truth — pages mounted under PublicOnly don't need their
 * own "am I signed in?" defensive checks.
 */
export function PublicOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth()

  if (status === 'loading') return <FullPageSpinner />
  if (status === 'authenticated') return <Navigate to="/app" replace />
  // 'unauthenticated' OR 'unconfigured' — let the page render.
  return <>{children}</>
}
